import { AdSize, adSizes } from '../../components/presentational/Ad';
import theme from '../../lib/theme';
const { mediaSizes } = theme;
const {
  video,
  mobileLeaderboard,
  mediumRectangle,
  largeRectangle,
  largeLeaderboard,
  halfPage,
  leaderboard,
  smallBillboard,
  mediumBillboard,
  billboard,
  fluid,
  videoDesktop,
  videoMobile,
  shortBanner,
  mobileBanner,
  wideSkyScraper,
} = adSizes;

const HIDDEN_ADHESION = process.env.HIDDEN_ADHESION;

const sponsoredInarticle: AdSize = {
  sizeMapping: [
    {
      viewport: [leaderboard[0], 0],
      slot: [mediumBillboard],
    },
    { viewport: [0, 0], slot: [mediumRectangle] },
  ],
};

/**
 * In-article display slots (standard_inarticle1 / standard_inarticle2).
 *
 * Every creative must fit the article text column, which is
 * layoutWidths.contentPane (1160) - layoutWidths.sidebar (340) = 820px MAX — and
 * it only reaches 820px on wide viewports. At a 728px viewport the column is only
 * ~330px, so wider sizes step in at mediaSizes.l, not at 728. A 970x250 never
 * fits and is deliberately absent.
 *
 * NO half-page on MOBILE (removed 2026-07): a fixed 600px unit fills ~85% of a
 * mobile viewport, and the post-AdSparc 300x600 premium (~A$2.7/day across both
 * units) no longer pays for that scroll cost. halfPage stays on desktop, where
 * the wider layout absorbs it.
 *
 * 336x280, 728x90 and 728x190 were removed from the request entirely: GAM shows
 * ZERO rendered impressions for them in these slots, ever — dead weight in every
 * bid request.
 */
const standardInarticle: AdSize = {
  sizeMapping: [
    { viewport: [0, 0], slot: [fluid, mediumRectangle] },
    // Text column is comfortably past 300px here; desktop keeps the half-page.
    { viewport: [mediaSizes.l, 0], slot: [fluid, mediumRectangle, halfPage] },
  ],
};

const siderail: AdSize = {
  sizeMapping: [
    { viewport: [0, 0], slot: [] },
    { viewport: [leaderboard[0], 0], slot: [halfPage, mediumRectangle] },
  ],
};

//#region Game Ad Sizes
const game_siderail: AdSize = {
  sizeMapping: [
    { viewport: [0, 0], slot: [] },
    { viewport: [mediaSizes.xl, 0], slot: [wideSkyScraper] },
    { viewport: [mediaSizes.xxl, 0], slot: [halfPage] },
  ],
};

const game_incontent: AdSize = {
  sizeMapping: [
    { viewport: [0, 0], slot: [fluid, mediumRectangle] },
    { viewport: [mediaSizes.xl, 0], slot: [], },
  ],
};

const game_top: AdSize = {
  sizeMapping: [
    { viewport: [0, 0], slot: [mobileBanner, mobileLeaderboard], },
    { viewport: [mediaSizes.s, 0], slot: [leaderboard] },
    { viewport: [mediaSizes.xl, 0], slot: [mediumBillboard, leaderboard], },
  ],
};

const game_billboard: AdSize = {
  sizeMapping: [
    { viewport: [0, 0], slot: [mobileBanner, mobileLeaderboard], },
    { viewport: [mediaSizes.s, 0], slot: [leaderboard] },
    { viewport: [mediaSizes.l, 0], slot: [mediumBillboard, leaderboard, billboard], },
  ],
};
//#endregion Game Ad Sizes

const adhesion: AdSize = {
  sizeMapping: [
    { viewport: [0, 0], slot: [mobileLeaderboard] },
    { viewport: [mediaSizes.m, 0], slot: [leaderboard] },
  ],
};

const billboardAd: AdSize = {
  sizeMapping: [
    {
      viewport: [0, 0],
      slot: [mediumRectangle],
    },
    {
      viewport: [mediaSizes.m, 0],
      slot: [mediumRectangle, leaderboard],
    },
    {
      viewport: [mediaSizes.l, 0],
      slot: [mediumRectangle, leaderboard, billboard],
    },
  ],
};

const config: { [key: string]: AdSize } = {
  standard_billboard: billboardAd,

  standard_inarticle_native: {
    sizeMapping: [
      {
        viewport: [0, 0],
        slot: [fluid],
      },
    ],
  },

  standard_recirculation_native1: {
    sizeMapping: [
      {
        viewport: [0, 0],
        slot: [fluid],
      },
    ],
  },

  standard_recirculation_native2: {
    sizeMapping: [
      {
        viewport: [0, 0],
        slot: [fluid],
      },
    ],
  },

  // Despite the name this is NOT a video slot — there is no VAST player, only a display
  // slot with video-shaped sizes. With "Enable explicit targeting" now off it fills with
  // AdX *display* backfill, but 320x180 and 640x360 are non-standard IAB sizes with thin
  // demand (A$0.53 across 30 days). mediumRectangle is added because the GAM ad unit
  // already has 300x250 registered — a core size with real demand, at no cost.
  standard_inarticle_video: {
    sizeMapping: [
      {
        viewport: [0, 0],
        slot: [videoMobile, mediumRectangle, video],
      },
      {
        viewport: [1024, 0],
        slot: [videoDesktop, mediumRectangle, video],
      },
    ],
  },

  standard_high_impact: {
    sizeMapping: [
      {
        viewport: [0, 0],
        slot: [video],
      },
    ],
  },


  standard_inarticle1: standardInarticle,
  standard_inarticle2: standardInarticle,
  standard_siderail1: siderail,
  standard_siderail2: siderail,
  standard_adhesion: HIDDEN_ADHESION
    ? {
        sizeMapping: [
          {
            viewport: [0, 0],
            slot: [video],
          },
        ],
      }
    : adhesion,

  sponsored_siderail1: siderail,
  sponsored_siderail2: siderail,
  sponsored_inarticle1: sponsoredInarticle,
  sponsored_inarticle2: sponsoredInarticle,
  sponsored_adhesion: adhesion,

  game_adhesion: adhesion,
  game_top: game_top,
  game_siderail1: game_siderail,
  game_siderail2: game_siderail,
  game_incontent1: game_incontent,
  game_incontent2: game_incontent,
  game_enterprise_billboard: game_billboard,
  game_recirculation_native1: billboardAd,
  game_recirculation_native2: billboardAd,
  game_billboard: {
    sizeMapping: [
      {
        viewport: [0, 0],
        slot: [leaderboard],
      },
      {
        viewport: [mediaSizes.m, 0],
        slot: [leaderboard],
      },
      {
        viewport: [mediaSizes.l, 0],
        slot: [leaderboard],
      },
    ],
  },
  
  standard_enterprise_billboard: billboardAd,
  standard_enterprise_inarticle_native: {
    sizeMapping: [
      {
        viewport: [0, 0],
        slot: [fluid],
      },
    ],
  },


  sponsored_inbetween: {
    sizeMapping: [
      {
        viewport: [0, 0],
        slot: [fluid, mediumRectangle],
      },
      {
        viewport: [mediaSizes.m, 0],
        slot: [fluid, mediumRectangle, mediumBillboard, leaderboard],
      },
      {
        viewport: [mediaSizes.l, 0],
        slot: [fluid, mediumRectangle, mediumBillboard, billboard],
      },
    ],
  },
};

export default config;
